import {
  all, call, put, takeLatest,
} from 'redux-saga/effects'
import { toast } from 'react-toastify'
import { PayloadAction } from '@reduxjs/toolkit'
import { actions as appActions } from '../state'
import { ReturnCall } from '../types'
import apis from './DockManagementApi'
import { actions } from './DockManagementState'
import {
  CreateNewDockParams, GetDockPayload, SetDockIsExpressPayload, SetDockIsFreshPayload, GetDocksPayload,
} from './DockManagementTypes'
import { sortDocks } from './DockManagementUtils'

export function* getDocks(action?: PayloadAction<GetDocksPayload>) {
  yield put(appActions.isLoading(true))

  try {
    const fc_id = action?.payload?.fc_id
    const result: ReturnCall<typeof apis.getAllDocks> = yield call(apis.getAllDocks, fc_id)
    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar informações das Docas!')
      return
    }

    yield put(actions.updateDockManagementField({ key: 'docks', value: sortDocks(result.data) }))
  } catch (error: any) {
    toast.error('Falha ao buscar informações das Docas!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* patchDock({ payload }: PayloadAction<number>) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.setDockFree> = yield call(apis.setDockFree, payload)

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao liberar a doca!')
      return
    }

    yield put(actions.setDockFree(result.data))
  } catch (error: any) {
    toast.error('Falha ao liberar a doca!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* createNewDock({ payload }: PayloadAction<CreateNewDockParams>) {
  yield put(appActions.isLoading(true))

  try {
    const { onSuccess, ...params } = payload
    const result: ReturnCall<typeof apis.createNewDock> = yield call(apis.createNewDock, params)

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao criar nova doca!')
      return
    }

    yield put(actions.addDock(result.data))
    onSuccess?.()
    toast.success('Doca criada com sucesso!')
  } catch (error: any) {
    toast.error('Falha ao criar nova doca!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* setDockIsFresh(data: PayloadAction<SetDockIsFreshPayload>) {
  yield put(appActions.isLoading(true))

  const { onSuccess, onError } = data.payload

  try {
    const result: ReturnCall<typeof apis.setDockIsFresh> = yield call(
      apis.setDockIsFresh,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao alterar tipo da doca')
      onError()
      return
    }

    yield put(actions.updateDockConfig({
      id: result.data.id,
      isFresh: result.data.isFresh,
    }))

    onSuccess()
  } catch (error: any) {
    toast.error('Falha ao alterar tipo da doca!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* setDockIsExpress(data: PayloadAction<SetDockIsExpressPayload>) {
  yield put(appActions.isLoading(true))

  const {
    id, isExpress, onSuccess, onError,
  } = data.payload

  try {
    const result: ReturnCall<typeof apis.setDockIsExpress> = yield call(apis.setDockIsExpress, {
      id,
      isExpress,
    })

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao alterar a configuração da doca')
      onError()
      return
    }

    yield put(actions.updateDockConfig({
      id: result.data.id,
      isExpress: result.data.isExpress,
    }))

    onSuccess()
  } catch (error: any) {
    toast.error('Falha ao alterar a configuração da doca!')
    onError()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getDock(data: PayloadAction<GetDockPayload>) {
  yield put(appActions.isLoading(true))

  const { id, onSuccess, onError } = data.payload

  try {
    const result: ReturnCall<typeof apis.getDock> = yield call(apis.getDock, id)

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Falha ao buscar informações da doca')
      onError?.()
      return
    }

    onSuccess(result.data)
  } catch (error: any) {
    toast.error('Falha ao buscar informações da doca!')
    onError?.()
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getDocks.type, getDocks),
    takeLatest(actions.patchDock.type, patchDock),
    takeLatest(actions.createNewDock.type, createNewDock),
    takeLatest(actions.setDockIsFresh.type, setDockIsFresh),
    takeLatest(actions.setDockIsExpress.type, setDockIsExpress),
    takeLatest(actions.getDock.type, getDock),
  ])
}
